import { useState, useMemo } from 'react';
import { X, MessageSquare, Users, PlusCircle, MinusCircle, ChevronLeft } from '@/components/icons';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { PrntrCard, PrntrInput, prntrInputClass } from './ui/prntr-input';
import { SearchInput } from './ui/search-input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { useCurrentCrewMember } from '../hooks/useCurrentCrewMember';
import { useCrewGetAll } from '../lib/hooks';
import { useNavigate } from '@tanstack/react-router';
import { useCreateConversation, useConversations } from '../lib/convex-hooks';
import { toast } from 'sonner';
import { CrewMemberPickerDrawer } from './CrewMemberPickerDrawer';
import { getInitials, formatCrewRole } from '../lib/utils';

interface NewChatDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewChatDrawer({
  open,
  onOpenChange,
}: NewChatDrawerProps) {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentCrewMember();
  const { data: allCrew = [] } = useCrewGetAll();
  const createConversation = useCreateConversation();
  const convexConversations = useConversations(currentUser?.companyId, currentUser?.id);

  const [view, setView] = useState<'menu' | 'group'>('menu');
  const [groupName, setGroupName] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [showDMPicker, setShowDMPicker] = useState(false);
  const [crewSearchQuery, setCrewSearchQuery] = useState('');

  // Get crew members excluding current user
  const crewMembers = useMemo(() => {
    const currentUserId = currentUser?.id;

    return allCrew
      .filter((member: any) => {
        if (currentUserId && member.id === currentUserId) return false;
        return true;
      })
      .map((member: any) => ({
        id: member.id,
        name: member.name || 'Unknown',
        firstName: member.firstName,
        lastName: member.lastName,
        role: member.role || formatCrewRole(member.crewRole) || 'Crew Member',
        avatarColor: member.avatarColor,
      }));
  }, [allCrew, currentUser]);

  // Filter crew members by search query
  const filteredCrewMembers = useMemo(() => {
    if (!crewSearchQuery.trim()) return crewMembers;
    const query = crewSearchQuery.toLowerCase();
    return crewMembers.filter((member) => {
      const displayName = member.firstName && member.lastName
        ? `${member.firstName} ${member.lastName}`
        : member.name;
      return displayName.toLowerCase().includes(query) ||
        member.role.toLowerCase().includes(query);
    });
  }, [crewMembers, crewSearchQuery]);

  const handleReset = () => {
    setView('menu');
    setGroupName('');
    setSelectedMemberIds([]);
    setCrewSearchQuery('');
  };

  const handleClose = () => {
    handleReset();
    onOpenChange(false);
  };

  const handleCreateDM = async (crewId: string) => {
    const participant = crewMembers.find((m) => m.id === crewId);
    if (!participant) return;

    // Check if conversation already exists
    const threads = convexConversations || [];
    const existingThread = threads.find(
      (thread: any) => !thread.isGroup &&
      thread.otherParticipants?.some((p: any) => p.crewId === participant.id)
    );

    if (existingThread) {
      navigate({ to: `/conversations/${existingThread._id}` });
      handleReset();
      onOpenChange(false);
      return;
    }

    // Create new conversation
    try {
      const conversation = await createConversation({
        companyId: currentUser!.companyId,
        name: participant.name,
        isGroup: false,
        participantIds: [currentUser!.id, participant.id],
        crewId: currentUser!.id,
      });
      navigate({ to: `/conversations/${conversation}` });
      handleReset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating DM:', error);
      toast.error('Failed to create direct message');
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedMemberIds.length === 0) return;

    try {
      const members = crewMembers.filter(m => selectedMemberIds.includes(m.id));
      const conversation = await createConversation({
        companyId: currentUser!.companyId,
        name: groupName,
        isGroup: true,
        participantIds: [currentUser!.id, ...members.map(m => m.id)],
        crewId: currentUser!.id,
      });
      navigate({ to: `/conversations/${conversation}` });
      handleReset();
      onOpenChange(false);
      toast.success('Group created successfully');
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    }
  };

  const toggleMember = (memberId: string) => {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const getTitle = () => {
    if (view === 'menu') return 'New Chat';
    return 'New Group';
  };

  return (
    <>
      {/* DM Picker Drawer */}
      <CrewMemberPickerDrawer
        isOpen={showDMPicker}
        onClose={() => setShowDMPicker(false)}
        mode="single"
        title="New Direct Message"
        onSelect={handleCreateDM}
        excludeCrewIds={currentUser?.id ? [currentUser.id] : []}
      />


      <Drawer open={open} onOpenChange={handleClose}>
        <DrawerContent className="bg-surface-L0 rounded-tl-2xl rounded-tr-2xl shadow-lg max-h-[85dvh] flex flex-col">
          {/* Dialog Header */}
          <DrawerHeader className="flex items-center justify-between p-4">
            {view !== 'menu' && (
              <button
                onClick={() => setView('menu')}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Back"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <DrawerTitle className="text-[18px] font-semibold text-content-primary-bold font-primary flex-1 text-center">
              {getTitle()}
            </DrawerTitle>
            <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleClose}>
              <X className="w-4 h-4" />
            </Button>
          </DrawerHeader>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col">
            {view === 'menu' && (
              <div className="px-2 py-2">
                <div className="flex flex-col gap-2 p-2">
                  {/* New Direct Message */}
                  <button
                    onClick={() => setShowDMPicker(true)}
                    className="flex items-center gap-2 px-2 py-[5.5px] min-h-8 rounded-md hover:bg-gray-50 transition-colors text-left w-full"
                  >
                    <MessageSquare className="w-5 h-5 text-content-primary-bold shrink-0" />
                    <p className="text-sm text-content-primary-bold font-primary leading-normal tracking-[0.07px]">
                      New Direct Message
                    </p>
                  </button>

                  {/* Separator */}
                  <div className="w-full h-px bg-border-L1" />

                  {/* New Group */}
                  <button
                    onClick={() => setView('group')}
                    className="flex items-center gap-2 px-2 py-[5.5px] min-h-8 rounded-md hover:bg-gray-50 transition-colors text-left w-full"
                  >
                    <Users className="w-5 h-5 text-content-primary-bold shrink-0" />
                    <p className="text-sm text-content-primary-bold font-primary leading-normal tracking-[0.07px]">
                      New Group
                    </p>
                  </button>
                </div>
              </div>
            )}

            {view === 'group' && (
              <>
                <div className="px-4 pt-4 pb-2 shrink-0 space-y-4">
                  {/* Group Name */}
                  <PrntrCard>
                    <PrntrInput label="Group Name" empty={!groupName}>
                      <input
                        id="groupName"
                        type="text"
                        placeholder="Enter group name..."
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        className={prntrInputClass}
                      />
                    </PrntrInput>
                  </PrntrCard>

                  {/* Select Crew */}
                  <div>
                    <Label className="text-sm font-medium text-content-primary-bold">
                      Select Crew
                    </Label>
                    <SearchInput
                      placeholder="Search crew"
                      value={crewSearchQuery}
                      onChange={(e) => setCrewSearchQuery(e.target.value)}
                      onClear={() => setCrewSearchQuery('')}
                      enterKeyHint="search"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Crew List */}
                <div className="max-h-[300px] overflow-auto px-4">
                  <div className="flex flex-col gap-2">
                    {filteredCrewMembers.map((member, index) => {
                      const isSelected = selectedMemberIds.includes(member.id);
                      const initials = getInitials(member.firstName, member.lastName);
                      const displayName =
                        member.firstName && member.lastName
                          ? `${member.firstName} ${member.lastName}`
                          : member.name;

                      return (
                        <>
                          {index > 0 && <div className="h-px bg-border" />}
                          <button
                            key={member.id}
                            onClick={() => toggleMember(member.id)}
                            className={`flex items-center gap-3 py-3 w-full text-left transition-colors rounded-lg ${
                              isSelected ? 'bg-background-brand-subtle' : 'hover:bg-surface-L2'
                            }`}
                          >
                            <Avatar className="size-10 shrink-0">
                              <AvatarFallback className="text-background" style={member.avatarColor ? { backgroundColor: member.avatarColor } : undefined}>{initials}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-content-primary-bold truncate">
                                {displayName}
                              </p>
                              <p className="text-xs text-content-primary-subtle truncate">
                                {member.role}
                              </p>
                            </div>
                            {isSelected ? (
                              <MinusCircle className="size-5 text-content-primary-bold shrink-0" />
                            ) : (
                              <PlusCircle className="size-5 text-content-primary-bold shrink-0" />
                            )}
                          </button>
                        </>
                      );
                    })}
                  </div>
                </div>

                {/* Footer with Cancel and Create buttons */}
                <DrawerFooter className="border-t">
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="mini"
                      className="flex-1"
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="mini"
                      className="flex-1"
                      onClick={handleCreateGroup}
                      disabled={!groupName.trim() || selectedMemberIds.length === 0}
                    >
                      Create
                    </Button>
                  </div>
                </DrawerFooter>
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
